/**
 * Настройка интерфейса, которая переживает перезагрузку страницы.
 *
 * Речь только о предпочтениях показа: выбранное представление, режим
 * календаря, плотность таблицы. Проектные данные так не хранятся —
 * они живут на сервере и должны быть одинаковыми у всей команды.
 *
 * Хранилище может быть недоступно (приватный режим, отключённые cookie,
 * переполненная квота). Это не повод падать: настройка просто не запомнится.
 */

import { useCallback, useState } from 'react';

const PREFIX = 'project-os:';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function useLocalSetting<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => read(key, fallback));

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(PREFIX + key, JSON.stringify(next));
      } catch {
        // Настройка не запомнится — работать это не мешает.
      }
    },
    [key],
  );

  return [value, update];
}

/**
 * Список недавно открытых элементов.
 *
 * Нужен командной строке: чаще всего человек возвращается к тому, с чем
 * работал минуту назад, а не ищет новое.
 */
const RECENT_KEY = 'recent-items';
const RECENT_LIMIT = 5;

export interface RecentItem {
  id: string;
  projectId: string;
  title: string;
  type: string;
}

export function readRecentItems(projectId: string): RecentItem[] {
  return read<RecentItem[]>(RECENT_KEY, []).filter((entry) => entry.projectId === projectId);
}

/** Запоминает открытый элемент. Повтор поднимается наверх, а не дублируется. */
export function rememberRecentItem(entry: RecentItem): void {
  try {
    const previous = read<RecentItem[]>(RECENT_KEY, []).filter((item) => item.id !== entry.id);
    const next = [entry, ...previous].slice(0, RECENT_LIMIT * 4);
    localStorage.setItem(PREFIX + RECENT_KEY, JSON.stringify(next));
  } catch {
    // Недоступное хранилище не должно мешать открыть элемент.
  }
}
